import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API from '../config/api';
import { useClinicContext } from './ClinicContext';
import { useDoctorContext } from './DoctorContext';
import { InventoryMovement } from './type';

interface InventoryItem {
    _id: string;
    clinic_id: string;
    doctor_id: string;
    name: string;
    category: string;
    quantity: number;
    unit: string;
    purchasePrice: number;
    sellingPrice: number;
    billableByDefault: boolean;
    supplier?: string;
    expirationDate?: string;
    minStockLevel: number;
    notes?: string;
    createdAt: string;
    updatedAt: string;
}

interface DeductConsumable {
    inventory_id: string;
    quantity: number;
    notes?: string;
}

interface DeductForServiceParams {
    consumables: DeductConsumable[];
    patient_id: string;
    patient_name?: string;
    service_id?: string;
    service_name?: string;
    package_id?: string;
    package_name?: string;
    action_type?: 'manual' | 'service' | 'package' | 'billing';
}

interface InventoryContextType {
    items: InventoryItem[];
    loading: boolean;
    history: InventoryMovement[];
    historyLoading: boolean;
    fetchItems: () => Promise<void>;
    addItem: (item: Partial<InventoryItem>) => Promise<void>;
    updateItem: (id: string, item: Partial<InventoryItem>) => Promise<void>;
    deleteItem: (id: string) => Promise<void>;
    recordUsage: (id: string, patientId: string, quantity: number, notes?: string) => Promise<void>;
    deductForService: (params: DeductForServiceParams) => Promise<{ results: any[]; errors: any[] }>;
    fetchClinicHistory: (options?: { page?: number; limit?: number; action_type?: string }) => Promise<void>;
    getLowStockItems: () => InventoryItem[];
}

const InventoryContext = createContext<InventoryContextType | undefined>(undefined);

export const InventoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<InventoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState<InventoryMovement[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const { selectedClinicId } = useClinicContext();
    const { doctorId } = useDoctorContext();

    const fetchItems = useCallback(async () => {
        if (!selectedClinicId) return;
        try {
            setLoading(true);
            const response = await axios.get(`${API.BASE_URL}/api/inventory/${selectedClinicId}`);
            setItems(response.data.items || []);
        } catch (error) {
            console.error('Error fetching inventory items:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedClinicId]);

    const addItem = async (item: Partial<InventoryItem>) => {
        try {
            await axios.post(`${API.BASE_URL}/api/inventory`, {
                ...item,
                clinic_id: selectedClinicId,
                doctor_id: doctorId
            });
            await fetchItems();
        } catch (error) {
            console.error('Error adding inventory item:', error);
            throw error;
        }
    };

    const updateItem = async (id: string, item: Partial<InventoryItem>) => {
        try {
            await axios.put(`${API.BASE_URL}/api/inventory/${id}`, item);
            await fetchItems();
        } catch (error) {
            console.error('Error updating inventory item:', error);
            throw error;
        }
    };

    const deleteItem = async (id: string) => {
        try {
            await axios.delete(`${API.BASE_URL}/api/inventory/${id}`);
            await fetchItems();
        } catch (error) {
            console.error('Error deleting inventory item:', error);
            throw error;
        }
    };

    const recordUsage = async (id: string, patientId: string, quantity: number, notes?: string) => {
        try {
            await axios.post(`${API.BASE_URL}/api/inventory/${id}/use`, {
                patient_id: patientId,
                quantity,
                usedBy: doctorId,
                notes
            });
            await fetchItems();
        } catch (error) {
            console.error('Error recording usage:', error);
            throw error;
        }
    };

    const deductForService = async (params: DeductForServiceParams): Promise<{ results: any[]; errors: any[] }> => {
        if (!selectedClinicId || !params.consumables || params.consumables.length === 0) {
            return { results: [], errors: [] };
        }
        try {
            const response = await axios.post(`${API.BASE_URL}${API.ENDPOINTS.INVENTORY_BULK_DEDUCT}`, {
                clinic_id: selectedClinicId,
                patient_id: params.patient_id,
                patient_name: params.patient_name || '',
                service_id: params.service_id || '',
                service_name: params.service_name || '',
                package_id: params.package_id || '',
                package_name: params.package_name || '',
                action_type: params.action_type || 'service',
                usedBy: doctorId,
                consumables: params.consumables
            });
            await fetchItems();
            return {
                results: response.data.results || [],
                errors: response.data.errors || []
            };
        } catch (error) {
            console.error('Error deducting for service:', error);
            throw error;
        }
    };

    const fetchClinicHistory = useCallback(async (options?: { page?: number; limit?: number; action_type?: string }) => {
        if (!selectedClinicId) return;
        try {
            setHistoryLoading(true);
            const params: any = {
                page: options?.page || 1,
                limit: options?.limit || 100
            };
            if (options?.action_type) params.action_type = options.action_type;
            const response = await axios.get(
                `${API.BASE_URL}${API.ENDPOINTS.INVENTORY_CLINIC_HISTORY(selectedClinicId)}`,
                { params }
            );
            setHistory(response.data.history || []);
        } catch (error) {
            console.error('Error fetching clinic history:', error);
        } finally {
            setHistoryLoading(false);
        }
    }, [selectedClinicId]);

    const getLowStockItems = () => {
        return items.filter(item => item.quantity <= item.minStockLevel);
    };

    useEffect(() => {
        if (selectedClinicId) {
            fetchItems();
        }
    }, [selectedClinicId, fetchItems]);

    return (
        <InventoryContext.Provider
            value={{
                items,
                loading,
                history,
                historyLoading,
                fetchItems,
                addItem,
                updateItem,
                deleteItem,
                recordUsage,
                deductForService,
                fetchClinicHistory,
                getLowStockItems
            }}
        >
            {children}
        </InventoryContext.Provider>
    );
};

export const useInventoryContext = () => {
    const context = useContext(InventoryContext);
    if (!context) {
        throw new Error('useInventoryContext must be used within InventoryProvider');
    }
    return context;
};
